import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { makeStyles } from 'styles/util';
import clsx from 'clsx';
import { useAppContext } from 'context/AppContext';
import strings from 'localization/strings';
import { getContact, getAddress } from 'helpers/ActorHelper';
import { createSetupFormInitialValues, sanitizeSetupFormValues, stripeFormIsIncomplete, getAvailableAndCurrentPaymentProviders, getSelectedPaymentProvider } from 'logic/paymentLogic';
import paymentProviders from 'enums/paymentProviders';
import contactTypes from 'enums/contactTypes';
import addressTypes from 'enums/addressTypes';

import StripeElementsWrapper from 'components/stripe/StripeElementsWrapper';
import FullScreenLoader from 'common/FullScreenLoader';
import Form from 'form/Form';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import Button from '@material-ui/core/Button';
import Typography from '@material-ui/core/Typography';
import PaymentElement from 'components/payment/PaymentElement';
import MarginBox from 'common/MarginBox';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';

const useStyles = makeStyles(({theme}) => ({
    actions: {
        flexWrap: 'wrap',
        gap: theme.spacing(2)
    },
    btnSmall: {
        height: '40px',
    },
    halfWidth: {
        flex: `0 0 calc(50% - ${theme.spacing(1)}px)`
    },
    mobileWidth: {
        [theme.breakpoints.down('sm')]: {
            width: 'calc(100% - 40px)',
            margin: '20px'
        }
    }
}));

const UpdatePaymentProviderDialog = ({ booking, open, onOk, onCancel }) => {
    const { appContext } = useAppContext();
    const classes = useStyles();

    const [isSaving, setIsSaving] = useState(false);
    const [paymentElementProvider, setPaymentElementProvider] = useState(undefined);
    const [showPaymentProviderSelector, setShowPaymentProviderSelector] = useState(true);

    const availablePaymentProviders = getAvailableAndCurrentPaymentProviders(booking);

    const selectedPaymentProvider = getSelectedPaymentProvider(booking, availablePaymentProviders);

    const stripeSetupBookingId = availablePaymentProviders.includes(paymentProviders.stripe.key)
        ? booking.id
        : undefined;

    const initialValues = {
        setup: createSetupFormInitialValues({
            paymentProvider: selectedPaymentProvider,
            billecta: {
                collectAddress: true,
                name: booking.tenantActor.name,
                email: getContact(booking.tenantActor, [contactTypes.invoiceEmail, contactTypes.mainEmail]),
                address: booking.invoiceAddress ?? getAddress(booking.tenantActor, addressTypes.invoice)
            }
        }, appContext)
    };

    const handleFormSubmit = values => {
        setIsSaving(true);
        onOk({
            values,
            onCompleted: () => setIsSaving(false)
        });
    };

    const handleSelectedPaymentProvider = provider => {
        setPaymentElementProvider(provider);
    };

    const handleShowPaymentProviderSelector = value => {
        setShowPaymentProviderSelector(value);
    };

    if(!open) {
        return null;
    }

    return (
        <StripeElementsWrapper setupBookingId={stripeSetupBookingId}>
            {
                ({ stripeSetupContext }) => (
                    <Dialog open={open} onClose={onCancel} disableEnforceFocus /* to be able to set focus to elements outside the dialog, e.g. when authorizing a payment method in an iframe provided by Stripe */>
                        <FullScreenLoader open={isSaving} />
                        <Form
                            initialValues={initialValues}
                            initialValuesEqual={() => true}
                            onSubmit={values => handleFormSubmit(sanitizeSetupFormValues(values, stripeSetupContext))}
                        >
                            {({ handleSubmit, values, invalid }) => (
                                <form onSubmit={handleSubmit}>
                                    <DialogTitle disableTypography>
                                        <Typography variant="h5">
                                            {strings.updatePaymentProvider}
                                        </Typography>
                                    </DialogTitle>
                                    <DialogContent>
                                        <Typography variant="body1">
                                            {strings.currentPaymentProvider}: {paymentProviders[booking.paymentProvider].shortTitle}
                                            {
                                                stripeSetupContext?.savedPaymentMethods?.length > 0 && (', ' + strings.formatString(strings.protectedCardDetails, stripeSetupContext.savedPaymentMethods[0].last4, stripeSetupContext.savedPaymentMethods[0].expirationMonth, stripeSetupContext.savedPaymentMethods[0].expirationYear))
                                            }
                                        </Typography>
                                        <MarginBox top={4}>
                                            <PaymentElement
                                                name="setup"
                                                paymentProviders={availablePaymentProviders}
                                                stripeSetupContext={stripeSetupContext}
                                                onSelectedPaymentProvider={handleSelectedPaymentProvider}
                                                showPaymentProviderSelector={showPaymentProviderSelector}
                                                changeIntended
                                            />
                                        </MarginBox>
                                    </DialogContent>
                                    <DialogActions disableSpacing className={classes.actions}>
                                        {
                                            showPaymentProviderSelector &&
                                            (
                                                <Button
                                                    type="button"
                                                    variant="contained"
                                                    color="primary"
                                                    fullWidth
                                                    onClick={() => handleShowPaymentProviderSelector(false)}
                                                    disabled={!paymentElementProvider}
                                                >
                                                    {strings.continue}
                                                </Button>
                                            )
                                        }
                                        {
                                            !showPaymentProviderSelector &&
                                            (
                                                <>
                                                    <Button
                                                        type="submit"
                                                        variant="contained"
                                                        color="primary"
                                                        fullWidth
                                                        disabled={invalid || stripeFormIsIncomplete(values.setup, stripeSetupContext)}
                                                    >
                                                        {strings.save}
                                                    </Button>
                                                    <Button
                                                        className={clsx(classes.btnSmall, classes.halfWidth)}
                                                        type="button"
                                                        variant="contained"
                                                        color="secondary"
                                                        onClick={() => handleShowPaymentProviderSelector(true)}
                                                    >
                                                        <ArrowBackIcon />{strings.goBack}
                                                    </Button>
                                                </>
                                            )
                                        }
                                        <Button
                                            className={clsx(classes.btnSmall, {[classes.halfWidth]: !showPaymentProviderSelector})}
                                            variant="contained"
                                            color="secondary"
                                            fullWidth={showPaymentProviderSelector}
                                            onClick={onCancel}
                                        >
                                            {strings.cancel}
                                        </Button>
                                    </DialogActions>
                                </form>
                            )}
                        </Form>
                    </Dialog>
                )
            }
        </StripeElementsWrapper>
    );
};

UpdatePaymentProviderDialog.propTypes = {
    booking: PropTypes.object.isRequired,
    open: PropTypes.bool.isRequired,
    onOk: PropTypes.func.isRequired,
    onCancel: PropTypes.func.isRequired
};

export default UpdatePaymentProviderDialog;
